import { useMemo, useState } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine, ReferenceArea, ZAxis } from 'recharts';
import { Target, Info, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGSCTopQueries } from '@/hooks/useGSCData';
import { useAuthStore } from '@/stores/auth-store';

interface QueryPoint {
  query: string;
  x: number; // CTR
  y: number; // Position (inverted for visual - lower position = higher on chart)
  z: number; // Impressions (bubble size)
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  quadrant: 'stars' | 'opportunities' | 'investigate' | 'improve';
}

/**
 * Opportunity Matrix View - Visual scatter plot of queries
 * X-axis: CTR (higher = better)
 * Y-axis: Position (lower = better, inverted visually)
 * Bubble size: Impressions
 *
 * Quadrants:
 * - Top Right (Stars): Good position + Good CTR
 * - Top Left (Opportunities): Good position + Low CTR
 * - Bottom Right (Investigate): Bad position + Good CTR (unusual)
 * - Bottom Left (Improve): Bad position + Low CTR
 */
export function OpportunityMatrixView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGSC = !!activeProperty?.gscSite;

  const { data: queries, isLoading } = useGSCTopQueries(100);
  const [hoveredQuery, setHoveredQuery] = useState<QueryPoint | null>(null);
  const [selectedQuadrant, setSelectedQuadrant] = useState<string | null>(null);

  // Transform data for scatter chart
  const chartData = useMemo(() => {
    if (!queries) return [];

    return queries.map((q): QueryPoint => {
      // Determine quadrant
      const isGoodPosition = q.position <= 10;
      const isGoodCTR = q.ctr >= 3;

      let quadrant: QueryPoint['quadrant'];
      if (isGoodPosition && isGoodCTR) quadrant = 'stars';
      else if (isGoodPosition && !isGoodCTR) quadrant = 'opportunities';
      else if (!isGoodPosition && isGoodCTR) quadrant = 'investigate';
      else quadrant = 'improve';

      return {
        query: q.query,
        x: q.ctr,
        y: 30 - q.position, // Invert so higher = better
        z: Math.sqrt(q.impressions) * 2, // Scale for visual
        clicks: q.clicks,
        impressions: q.impressions,
        ctr: q.ctr,
        position: q.position,
        quadrant,
      };
    });
  }, [queries]);

  const filteredData = selectedQuadrant
    ? chartData.filter((d) => d.quadrant === selectedQuadrant)
    : chartData;

  // Quadrant stats
  const quadrantStats = useMemo(() => ({
    stars: chartData.filter((d) => d.quadrant === 'stars').length,
    opportunities: chartData.filter((d) => d.quadrant === 'opportunities').length,
    investigate: chartData.filter((d) => d.quadrant === 'investigate').length,
    improve: chartData.filter((d) => d.quadrant === 'improve').length,
  }), [chartData]);

  const getColor = (quadrant: string) => {
    switch (quadrant) {
      case 'stars': return '#22c55e'; // Green
      case 'opportunities': return '#eab308'; // Yellow
      case 'investigate': return '#3b82f6'; // Blue
      case 'improve': return '#ef4444'; // Red
      default: return '#6b7280';
    }
  };

  if (!hasGSC) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No Search Console property connected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      <div>
        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <Target className="h-6 w-6 text-primary" />
          Opportunity Matrix
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Visual map of your search queries - find patterns and opportunities at a glance
        </p>
      </div>

      {/* Quadrant Legend/Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <QuadrantButton
          label="Stars"
          count={quadrantStats.stars}
          color="success"
          isSelected={selectedQuadrant === 'stars'}
          onClick={() => setSelectedQuadrant(selectedQuadrant === 'stars' ? null : 'stars')}
          description="High position + High CTR"
        />
        <QuadrantButton
          label="Opportunities"
          count={quadrantStats.opportunities}
          color="warning"
          isSelected={selectedQuadrant === 'opportunities'}
          onClick={() => setSelectedQuadrant(selectedQuadrant === 'opportunities' ? null : 'opportunities')}
          description="High position + Low CTR"
        />
        <QuadrantButton
          label="Investigate"
          count={quadrantStats.investigate}
          color="blue"
          isSelected={selectedQuadrant === 'investigate'}
          onClick={() => setSelectedQuadrant(selectedQuadrant === 'investigate' ? null : 'investigate')}
          description="Low position + High CTR"
        />
        <QuadrantButton
          label="Improve"
          count={quadrantStats.improve}
          color="danger"
          isSelected={selectedQuadrant === 'improve'}
          onClick={() => setSelectedQuadrant(selectedQuadrant === 'improve' ? null : 'improve')}
          description="Low position + Low CTR"
        />
        {selectedQuadrant && (
          <button
            onClick={() => setSelectedQuadrant(null)}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <Filter className="h-3 w-3" />
            Clear filter
          </button>
        )}
      </div>

      {/* Chart */}
      <div className="glass-panel p-4 flex-1 min-h-[400px]">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground animate-pulse">
            Loading matrix...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 40 }}>
              {/* Quadrant backgrounds */}
              <ReferenceArea x1={0} x2={3} y1={20} y2={30} fill="#22c55e" fillOpacity={0.05} />
              <ReferenceArea x1={3} x2={20} y1={20} y2={30} fill="#eab308" fillOpacity={0.05} />
              <ReferenceArea x1={0} x2={3} y1={0} y2={20} fill="#ef4444" fillOpacity={0.05} />
              <ReferenceArea x1={3} x2={20} y1={0} y2={20} fill="#3b82f6" fillOpacity={0.05} />

              {/* Axis lines for quadrants */}
              <ReferenceLine y={20} stroke="#374151" strokeDasharray="3 3" />
              <ReferenceLine x={3} stroke="#374151" strokeDasharray="3 3" />

              <XAxis
                type="number"
                dataKey="x"
                name="CTR"
                domain={[0, 'dataMax']}
                tickFormatter={(v) => `${v.toFixed(0)}%`}
                label={{ value: 'Click-Through Rate', position: 'bottom', offset: 10, fill: '#9ca3af' }}
                stroke="#4b5563"
                tick={{ fill: '#9ca3af' }}
              />
              <YAxis
                type="number"
                dataKey="y"
                name="Position"
                domain={[0, 30]}
                tickFormatter={(v) => `#${30 - v}`}
                label={{ value: 'Search Position', angle: -90, position: 'left', offset: -10, fill: '#9ca3af' }}
                stroke="#4b5563"
                tick={{ fill: '#9ca3af' }}
              />
              <ZAxis type="number" dataKey="z" range={[20, 400]} />
              <Tooltip content={<CustomTooltip />} />
              <Scatter
                data={filteredData}
                onMouseEnter={(data) => setHoveredQuery(data as QueryPoint)}
                onMouseLeave={() => setHoveredQuery(null)}
              >
                {filteredData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getColor(entry.quadrant)}
                    fillOpacity={hoveredQuery?.query === entry.query ? 1 : 0.7}
                    stroke={hoveredQuery?.query === entry.query ? '#fff' : 'none'}
                    strokeWidth={2}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Info Panel */}
      <div className="glass-panel p-4">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-primary mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">How to read this chart:</p>
            <ul className="space-y-1 text-xs">
              <li><span className="text-success">Stars</span>: Your best performers - maintain these</li>
              <li><span className="text-warning">Opportunities</span>: Good rankings but low CTR - improve titles/descriptions</li>
              <li><span className="text-blue-400">Investigate</span>: Unusual pattern - high CTR despite low ranking</li>
              <li><span className="text-danger">Improve</span>: Need content improvements to rank and attract clicks</li>
              <li>Bubble size represents search volume (impressions)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

interface QuadrantButtonProps {
  label: string;
  count: number;
  color: 'success' | 'warning' | 'blue' | 'danger';
  isSelected: boolean;
  onClick: () => void;
  description: string;
}

function QuadrantButton({ label, count, color, isSelected, onClick, description }: QuadrantButtonProps) {
  const colorStyles = {
    success: 'border-success bg-success/10 text-success',
    warning: 'border-warning bg-warning/10 text-warning',
    blue: 'border-blue-500 bg-blue-500/10 text-blue-400',
    danger: 'border-danger bg-danger/10 text-danger',
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-2 rounded-lg border-2 transition-all',
        isSelected ? colorStyles[color] : 'border-border bg-surface hover:bg-muted'
      )}
      title={description}
    >
      <div className="flex items-center gap-2">
        <span className={cn(
          'w-3 h-3 rounded-full',
          color === 'success' && 'bg-success',
          color === 'warning' && 'bg-warning',
          color === 'blue' && 'bg-blue-500',
          color === 'danger' && 'bg-danger',
        )} />
        <span className={cn('text-sm font-medium', isSelected ? '' : 'text-foreground')}>
          {label}
        </span>
        <span className={cn('text-xs', isSelected ? '' : 'text-muted-foreground')}>
          ({count})
        </span>
      </div>
    </button>
  );
}

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload || !payload[0]) return null;

  const data = payload[0].payload as QueryPoint;

  return (
    <div className="glass-panel p-3 shadow-lg border border-border">
      <p className="font-medium text-foreground text-sm truncate max-w-[200px]">{data.query}</p>
      <div className="mt-2 space-y-1 text-xs">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Position:</span>
          <span className="text-foreground font-medium">#{data.position.toFixed(1)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">CTR:</span>
          <span className="text-foreground font-medium">{data.ctr.toFixed(1)}%</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Impressions:</span>
          <span className="text-foreground font-medium">{data.impressions.toLocaleString()}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Clicks:</span>
          <span className="text-foreground font-medium">{data.clicks.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}
